/// <amd-module name="Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownChartOptionsFactory" />
import { DateRange, TeamMemberCapacity, TeamSettingsDaysOff } from "Dashboards/Clients/contracts/Work";
import { ChartProps, ChartTypesConstants, ClickEvent, HybridChartOptions } from "VSS/Features/Charts/Contracts";
import { AxisPlotBand } from "VSS/Features/Charts/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssThemeService } from "VSS/Platform/Theming";
import { DayOfWeek, parseIsoDateStringInLocalTime, shiftToUtc, toLocalDateIsoString } from "WidgetCommon/DateUtils/DateUtils";
import { ChartOptionFactoryBase } from "WidgetCommon/ModernWidgetTypes/ChartOptionFactoryBase";
import * as Resources from "Widgets/AnalyticsSprintBurndown/Resources";
import { BurnChartInputs, BurndownChartOptionFactory } from "Widgets/Burndown/BurndownWidgetComponents/BurndownChartOptionFactory";
import { getPeakScope } from "./TotalWorkProjectionHelper";

export interface AnalyticsSprintBurndownChartInputs extends BurnChartInputs {
    showNonWorkingDays?: boolean;
    showRemainingCapacityAtStartOfDay?: boolean;
    teamMemberCapacities: TeamMemberCapacity[];
    teamDaysOff: TeamSettingsDaysOff;
    teamWorkingDays: DayOfWeek[];
    sampleDates: string[];
    isMinimalChart?: boolean;
    showResolvedItemsAsCompleted?: boolean;
}

export class AnalyticsSprintBurndownChartOptionsFactory extends ChartOptionFactoryBase<AnalyticsSprintBurndownChartInputs> {
    constructor(private pageContext: IVssPageContext) {
        super();
    }

    public createChartOptions(featureChartData: AnalyticsSprintBurndownChartInputs): ChartProps {
        const defaultChartOptionFactory = new BurndownChartOptionFactory(this.pageContext);
        // Create default burndown chart options
        const chartOptions = defaultChartOptionFactory.createChartOptions(featureChartData);
        // Now customize that for ax sprint burndown specifics
        chartOptions.interpolateNullValues = false;
        chartOptions.xAxis!.formatAccessibleLabelsUsingAxisCategories = true;

        if (!featureChartData.isMinimalChart) {
            addTeamCapacityTrendData(
                chartOptions,
                featureChartData.teamMemberCapacities,
                featureChartData.teamDaysOff,
                featureChartData.teamWorkingDays,
                featureChartData.sampleDates,
                featureChartData.showRemainingCapacityAtStartOfDay
            );
            const peakScope = getPeakScope(
                featureChartData.remainingEffort,
                featureChartData.sampleDates,
                featureChartData.todayInAccountTimezone,
                featureChartData.showStackedWorkItemTypes,
                featureChartData.workItemTypeColorDictionary
            );
            addIdealTrendline(chartOptions, featureChartData.teamDaysOff, featureChartData.teamWorkingDays, featureChartData.sampleDates, peakScope);
        }
        // If the user shows non working days, annotate with plot bands, otherwise omit them entirely from the chart dataset.
        // In minimal chart we don't want to show plot bands , however we still want to remove nonWorkingDays to keep parity with full chart
        if (featureChartData.showNonWorkingDays) {
            if (!featureChartData.isMinimalChart) {
                const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
                const plotBandColor = themeService.getCurrentTheme()!.isDark ? "#c8c8c8" : "#dedede";

                chartOptions.xAxis!.plotBands = addPlotBandsForWeekendsAndOffDays(
                    featureChartData.sampleDates,
                    featureChartData.teamDaysOff,
                    featureChartData.teamWorkingDays,
                    plotBandColor
                );
            }
        } else {
            const teamDaysOff = (featureChartData.teamDaysOff as TeamSettingsDaysOff).daysOff;
            const sampleDatesWithoutNonWorkingDays: string[] = removeNonWorkingDaysFromChart(
                chartOptions,
                featureChartData.sampleDates,
                teamDaysOff,
                featureChartData.teamWorkingDays
            );
            // Overwrite sample dates after removing off days from sample dates to account for off days in click through behavior
            featureChartData.sampleDates = sampleDatesWithoutNonWorkingDays;
        }

        if (featureChartData.allowChartClicks) {
            chartOptions.click = (clickEvent: ClickEvent) => this.handleClick(clickEvent, featureChartData, defaultChartOptionFactory);
        }

        return chartOptions;
    }

    /***
     * Interprets the chart click event details for handling specific cases to AX sprint burndown widget and calls the underlying common handler for other cases
     */
    public handleClick(
        clickEvent: ClickEvent,
        chartInputs: AnalyticsSprintBurndownChartInputs,
        defaultChartOptionFactory: BurndownChartOptionFactory
    ): void {
        const seriesName = clickEvent.seriesName; // This is the localized name for the clicked series

        // Clicking on the team capacity trendline or the ideal trend line should never do anything.
        if (seriesName === Resources.TeamCapacityTrendlineTooltipLabel || seriesName === Resources.TeamCapacityIdealTrendLabel) {
            return;
        }

        defaultChartOptionFactory._handleClick(clickEvent, chartInputs);
    }
}

export function addPlotBandsForWeekendsAndOffDays(
    sampleDates: string[],
    teamDaysOff: TeamSettingsDaysOff,
    workingDays: DayOfWeek[],
    plotBandColor: string
): AxisPlotBand[] {
    const plotBands: AxisPlotBand[] = [];
    const shiftedTeamDaysOff = shiftDateRanges(teamDaysOff.daysOff);

    // Doing this in two passes now as team days off needs some manipulation before we can compare and sample dates does not have day information to compare weekends directly
    // First pass for off days
    for (let i = 0; i < shiftedTeamDaysOff.length; i++) {
        const startDate = toLocalDateIsoString(shiftedTeamDaysOff[i].start);
        let startIndex = sampleDates.indexOf(startDate);

        const endDate = toLocalDateIsoString(shiftedTeamDaysOff[i].end);
        let endIndex = sampleDates.indexOf(endDate);

        // If any one index is in the date range , then add band from that index to boundary of the sample dates
        if (startIndex !== -1 || endIndex !== -1) {
            startIndex = startIndex === -1 ? 0 : startIndex; // Clamp start index to 0 if it is out of bounds
            endIndex = endIndex === -1 ? sampleDates.length - 1 : endIndex;

            plotBands.push(generatePlotBand(startIndex, endIndex, plotBandColor));
        }
    }

    // Second pass for weekends
    for (let i = 0; i < sampleDates.length; i++) {
        const currentDate = new Date(sampleDates[i]);
        const day = shiftToUtc(currentDate).getDay();

        if (!isWorkingDay(workingDays, day)) {
            plotBands.push(generatePlotBand(i, i, plotBandColor));
        }
    }
    return plotBands;
}

function generatePlotBand(startDateIndex: number, endDateIndex: number, plotBandColor: string): AxisPlotBand {
    const currentPlotBand: AxisPlotBand = {
        start: startDateIndex - 1,
        end: endDateIndex,
        color: plotBandColor
    };
    return currentPlotBand;
}

function isWorkingDay(workingDays: DayOfWeek[], day: number): boolean {
    return workingDays.indexOf(day) !== -1;
}

/**
 * This conditionally adds a Team capacity trendline to the provided chartOptions object, assuming team capacity is specified.
 * Note: It is assumed that the date objects within teamMemberCapacities and teamDaysOff will be in UTC time
 *       This function will shift dates to local time for calculation.
 * @param chartOptions ChartProps that will recieve team capacity trendline data.
 * @param teamMemberCapacities list of team members with their capacity and days off.
 * @param teamDaysOff list of team shared days off. These days will not see any capacity changes
 * @param teamWorkingDays List of standard working days for the team. Weekends will not see any changes to capacity
 * @param sampleDates List of dates to calculate cumulative capacity for. Capacity is calculated as a burndown to 0 on the last day in the list.
 */
function addTeamCapacityTrendData(
    chartOptions: ChartProps,
    teamMemberCapacities: TeamMemberCapacity[],
    teamDaysOff: TeamSettingsDaysOff,
    teamWorkingDays: DayOfWeek[],
    sampleDates: string[],
    showRemainingCapacityAtStartOfDay?: boolean
): void {
    const shiftedTeamDaysOff = shiftDateRanges(teamDaysOff.daysOff);
    const shiftedCapacities = (teamMemberCapacities.map(x => {
        return {
            daysOff: shiftDateRanges(x.daysOff),
            activities: x.activities
        };
    }) as any) as TeamMemberCapacity[];
    const dates = sampleDates.map(x => parseIsoDateStringInLocalTime(x));
    const capacity = calculateTeamCapacity(dates, teamWorkingDays, shiftedTeamDaysOff, shiftedCapacities, showRemainingCapacityAtStartOfDay);
    // Omit capacity and the secondary axis if the team has no capacity data present (Many teams don't see value in populating their capacity each sprint)
    const hasAnyCapacity = capacity.some(item => {
        return item != null && item > 0;
    });
    if (hasAnyCapacity) {
        const capacitySeries = {
            name: Resources.TeamCapacityTrendlineTooltipLabel,
            data: capacity,
            color: "#6DB72C", // Legacy Burndown Chart's Capacity green.
            plotDashedStyle: true
        };
        chartOptions.series.push(capacitySeries);
        (chartOptions.specializedOptions as HybridChartOptions).chartTypes.push(ChartTypesConstants.Line);

        // Increase max y position to accomodate capacity which is maximal on first day.
        if (capacity[0] != null && chartOptions.yAxis != null && chartOptions.yAxis.max != null && capacity[0]! > chartOptions.yAxis.max) {
            chartOptions.yAxis.max = capacity[0]!;
        }
    }
}

function shiftDateRanges(dateRanges: DateRange[]): DateRange[] {
    return dateRanges.map(x => {
        return {
            start: shiftToUtc(x.start),
            end: shiftToUtc(x.end)
        };
    });
}

export function calculateTeamCapacity(
    dates: Date[],
    teamWorkingDays: DayOfWeek[],
    teamDaysOff: DateRange[],
    teamMemberCapacities: TeamMemberCapacity[],
    showRemainingCapacityAtStartOfDay?: boolean
): (number | null)[] {
    let cumulativeAvailabileCapacity = 0;
    const results = [];
    const clonedDates = dates.map(x => new Date(x.getTime()));
    // For consistency with legacy Sprint burndown experience, each day reflects a burndown on capacity at the end of the day.
    // Progress is reflected in terms of a reduction from the day before each working day, ending at 0. We accomplish this by accumulating capacity from 0, based on the next days work.
    let tommorow: Date | undefined = undefined;
    let currentDay: Date | undefined = undefined;
    for (const date of clonedDates.reverse()) {
        currentDay = showRemainingCapacityAtStartOfDay ? date : tommorow;
        if (currentDay != null && isTeamWorkingDay(currentDay, teamWorkingDays, teamDaysOff)) {
            // increase the remaining capacity to reflect work which will be made in the day ahead or consider capacity from start of the day
            cumulativeAvailabileCapacity += getCapacityOnDay(currentDay, teamMemberCapacities);
        }
        results.push(cumulativeAvailabileCapacity);
        tommorow = date;
    }
    return results.reverse();
}

function getCapacityOnDay(date: Date, teamMemberCapacities: TeamMemberCapacity[]): number {
    let capacityOfDay = 0;
    // Accumulate capacity of members working on the relevant day.
    for (const memberCapacity of teamMemberCapacities) {
        if (!isDayOff(date, memberCapacity.daysOff)) {
            memberCapacity.activities.forEach(x => (capacityOfDay += x.capacityPerDay));
        }
    }
    return capacityOfDay;
}

/**
 * Determines if a given day is a working day based on the team days off and the team working days.
 */
function isTeamWorkingDay(date: Date, teamWorkingDays: DayOfWeek[], teamDaysOff: DateRange[]): boolean {
    return isWorkingDay(teamWorkingDays, date.getDay()) && !isDayOff(date, teamDaysOff);
}

/**
 * checks a list of date ranges to see if the base date lies within any of those ranges.
 * @param date base date we are checking.
 * @param daysOff list of Date ranges representing the dayOff intervals.
 */
function isDayOff(date: Date, daysOff: DateRange[]): boolean {
    let isDayOff = false;
    for (const timePeriod of daysOff) {
        if (date.getTime() >= timePeriod.start.getTime() && date.getTime() <= timePeriod.end.getTime()) {
            isDayOff = true;
            break;
        }
    }
    return isDayOff;
}

/**
 * A two phase algorithm to purge out non-working days from a populated chart data set.
 * Part one identifies the day-indices which are recurring non-working days(e.g. weekend) & custom non-working days
 * Part two removes the list of date indices in reverse order (working from the back, so removals don't disrupt positions)
 */
function removeNonWorkingDaysFromChart(
    chartOptions: ChartProps,
    sampleDates: string[],
    teamdaysOff: DateRange[],
    workingDays: DayOfWeek[]
): string[] {
    const removalDateIndices = new Set<number>();
    const shiftedTeamDaysOff = shiftDateRanges(teamdaysOff);

    for (let i = 0; i < sampleDates.length; i++) {
        const currentDate = parseIsoDateStringInLocalTime(sampleDates[i]);

        if (!isTeamWorkingDay(currentDate, workingDays, shiftedTeamDaysOff)) {
            removalDateIndices.add(i);
        }
    }

    const sortedItems = Array.from(removalDateIndices).sort((a, b) => {
        return b - a;
    }); // Get items in reverse order
    sortedItems.forEach(i => {
        removeDatumFromAllSeries(chartOptions, i);
        removeDatumFromSampleDates(sampleDates, i);
    });

    return sampleDates;
}

/** Removes chart datum X-axis labels and from all series. This only works for charts using lock-step y values which do not indendently specify an x-coordinate.  */
function removeDatumFromAllSeries(chartOptions: ChartProps, index: number) {
    chartOptions.xAxis!.labelValues!.splice(index, 1);
    chartOptions.series.forEach(series => {
        series.data.splice(index, 1);
    });
}

/** Removes chart datum sample dates for off days . This is so that we handle click through behavior  */
function removeDatumFromSampleDates(sampleDates: string[], index: number) {
    sampleDates.splice(index, 1);
}

function getWorkingDayCount(dates: Date[], teamWorkingDays: DayOfWeek[], teamDaysOff: DateRange[]): number {
    let dayCount = 0;
    for (const date of dates) {
        dayCount += isTeamWorkingDay(date, teamWorkingDays, teamDaysOff) ? 1 : 0;
    }
    return dayCount;
}

export function calculateIdealTrendline(dates: Date[], teamWorkingDays: DayOfWeek[], teamDaysOff: DateRange[], totalWork: number): (number | null)[] {
    const dayCount = getWorkingDayCount(dates, teamWorkingDays, teamDaysOff);
    const idealSlope = dayCount > 0 ? totalWork / dayCount : 0;

    const results = [];
    // Build up the ideal slope from the end, based on the expected progress of the next day
    // This algorithm uses the same approach as the team capacity line, but is driven by constant slope, as opposed to variable capacity.
    let idealRemainingWork = 0;
    let tommorow: Date | undefined = undefined;
    const clonedDates = dates.map(x => new Date(x.getTime())).reverse();
    for (const date of clonedDates) {
        if (tommorow != null && isTeamWorkingDay(tommorow, teamWorkingDays, teamDaysOff)) {
            idealRemainingWork += idealSlope;
        }
        results.push(idealRemainingWork);
        tommorow = date;
    }
    return results.reverse();
}

function addIdealTrendline(
    chartOptions: ChartProps,
    teamDaysOff: TeamSettingsDaysOff,
    teamWorkingDays: DayOfWeek[],
    sampleDates: string[],
    totalWork: number
): void {
    const shiftedTeamDaysOff = shiftDateRanges(teamDaysOff.daysOff);
    const dates = sampleDates.map(x => parseIsoDateStringInLocalTime(x));
    const idealTrendData = calculateIdealTrendline(dates, teamWorkingDays, shiftedTeamDaysOff, totalWork);
    const greyTrendColor = "#9D9D9D"; // The same color as the stock Burndown trendline.

    const capacitySeries = {
        name: Resources.TeamCapacityIdealTrendLabel,
        data: idealTrendData,
        color: greyTrendColor
    };
    chartOptions.series.push(capacitySeries);
    (chartOptions.specializedOptions as HybridChartOptions).chartTypes.push(ChartTypesConstants.Line);

    // Increase max y position to accomodate total work which is maximal on first day.
    if (chartOptions.yAxis != null && chartOptions.yAxis.max != null && totalWork > chartOptions.yAxis.max) {
        chartOptions.yAxis.max = totalWork;
    }
}
